import React, { FC, ReactNode, Ref, useState, useEffect } from "react";
import Spinner from "react-spinkit";
import { IndicatorOptions } from "./scanview.types";
import { useGlobalContext } from "./ScanViewerContext";

import successPng from "../assets/success.png";
import errorPng from "../assets/error.png";
import frontCard from "../assets/front_card.png";
import backCard from "../assets/back_card.png";
import { createPortal } from "react-dom";

import ScanSwitch from "./scanSwitch";

import styles from "./scanFrame.module.scss";

import WebToMobileModal from "./webToMobileModal";
import MobilePortal from "../components/MobilePortal/MobilePortal";

type Props = {
  successIndicator?: ReactNode;
  errorIndicator?: ReactNode;
  handleRetryButton: () => void;
  frameDivRef: Ref<HTMLDivElement>;
  spinnerOptions: IndicatorOptions;
  webToMobileHandoffConfig?: {
    enabled?: boolean;
    urlBase?: string;
    companyName?: string;
  };
  cardScanApi?: any;
  backsideSupport?: boolean;
};

const ScanFrame: FC<Props> = ({
  successIndicator,
  handleRetryButton,
  frameDivRef,
  spinnerOptions,
  errorIndicator,
  webToMobileHandoffConfig,
  cardScanApi,
  backsideSupport,
}) => {
  const { stateMachineState, showCurtain, isMobile, isMobileOnLandscape } =
    useGlobalContext();

  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFlipped((f) => !f);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const shouldShowProgress = [
    "frontsideProcessing",
    "backsideProcessing",
    "autoCapture",
    "manualCapture",
    "backsidePending",
  ].some(stateMachineState.matches);

  const shouldShowRotateToScanBack =
    stateMachineState.matches("backsidePending") ||
    (stateMachineState.matches("backsideProcessing") &&
      !stateMachineState.context.processing);

  const isPending = ["autoCapture", "manualCapture"].some(
    stateMachineState.matches
  );

  const isProcessing =
    ["frontsideProcessing", "backsideProcessing"].some(
      stateMachineState.matches
    ) && stateMachineState.context.processing;

  const isProcessingAuto = isProcessing;

  const isProcessingManual =
    isProcessing &&
    !stateMachineState.context.autoScan &&
    stateMachineState.context.scannerRunning;

  const isErrorOrRetry = [
    "error",
    "cameraError",
    "retry",
    "eligibilityError",
  ].some(stateMachineState.matches);

  const greyCorner =
    ["manualCapture", "backsidePending"].some(stateMachineState.matches) &&
    !stateMachineState.context.autoScan;

  return (
    <>
      <div
        data-cy="cardscanCardGuide"
        className={`${styles.cardGuide} ${
          isMobile ? styles.mobile : ""
        } active ${
          stateMachineState.matches("manualCapture")
            ? "cardscan-card-guide-processing"
            : ""
        } ${isMobileOnLandscape && "card-guide-landscape"} ${
          stateMachineState.matches("success") ? styles.scanCompleted : ""
        }`}
        ref={frameDivRef}
      >
        {!showCurtain && !stateMachineState.context.processing && (
          <ScanSwitch />
        )}
        {shouldShowProgress && (
          <div
            className={`
          ${styles.corner} 
          ${styles.corner1} 
          ${greyCorner ? styles.cardscanCornerOff : ""}
          ${shouldShowProgress ? "cardscan-corner-active" : ""}`}
          />
        )}
        {shouldShowProgress && (
          <div
            className={`
          ${styles.corner} 
          ${styles.corner2}
          ${greyCorner ? styles.cardscanCornerOff : ""}
          ${shouldShowProgress ? "cardscan-corner-active" : ""}`}
          />
        )}
        {shouldShowProgress && (
          <div
            className={`
          ${styles.corner} 
          ${styles.corner3}
          ${greyCorner ? styles.cardscanCornerOff : ""}
          ${shouldShowProgress ? "cardscan-corner-active" : ""}`}
          />
        )}
        {shouldShowProgress && (
          <div
            className={`${styles.corner}  
          ${styles.corner4}
          ${greyCorner ? styles.cardscanCornerOff : ""}
          ${shouldShowProgress ? "cardscan-corner-active" : ""}`}
          />
        )}
        {stateMachineState.matches("success") && (
          <div className={styles.processStatus}>
            {successIndicator != null ? (
              successIndicator
            ) : (
              <img
                src={successPng}
                className={`${isMobile ? styles.mobile : ""} ${
                  isMobileOnLandscape ? styles.mobileLandscape : ""
                }`}
                alt="checkmark"
              />
            )}
            <MobilePortal elementId="mobileTopCurtain">
              <p
                data-cy="cardscanHelperText"
                id={styles.cardscanHelperText}
                className={`${isMobile ? styles.mobile : ""} ${
                  isMobileOnLandscape ? styles.mobileLandscape : ""
                }`}
              >
                {stateMachineState.context.headerTitle}
              </p>
            </MobilePortal>
          </div>
        )}
        {isErrorOrRetry && (!webToMobileHandoffConfig?.enabled || isMobile) && (
          <div className={styles.processStatus}>
            {errorIndicator != null ? (
              errorIndicator
            ) : (
              <img src={errorPng} className={`${styles.mobile}`} alt="" />
            )}
            <MobilePortal elementId="mobileTopCurtain">
              <p
                data-cy="cardscanHelperText"
                id={styles.cardscanHelperText}
                className={`${isMobile ? styles.mobile : ""} ${
                  isMobileOnLandscape ? styles.mobileLandscape : ""
                }`}
              >
                {stateMachineState.context.headerTitle}
              </p>
            </MobilePortal>
            {stateMachineState.matches("retry") && (
              <div
                className={`${styles.cardscanRetryButton} retryScanBtn`}
                onClick={handleRetryButton}
              >
                Retry Scan
              </div>
            )}
          </div>
        )}
        {isErrorOrRetry && !isMobile && webToMobileHandoffConfig?.enabled && (
          <WebToMobileModal
            enableModel={true}
            cardScanApi={cardScanApi}
            webToMobileHandoffConfig={webToMobileHandoffConfig}
            spinnerOptions={spinnerOptions}
            backsideSupport={backsideSupport}
          />
        )}
        {shouldShowRotateToScanBack && (
          <div
            className={`${styles.processStatus} ${styles.rotateImage} ${
              isMobile ? styles.mobile : ""
            }`}
          >
            <div
              className={`${styles.rotateImageContainer} ${
                isMobile ? styles.mobile : ""
              }`}
            >
              <img
                src={frontCard}
                className={styles.frontCard}
                style={{ maxWidth: "100px" }}
              />
              <img
                src={backCard}
                className={styles.backCard}
                style={{ maxWidth: "100px" }}
              />
            </div>
            <MobilePortal elementId="mobileTopCurtain">
              <p
                data-cy="cardscanHelperText"
                id={styles.cardscanHelperText}
                className={`${isMobile ? styles.mobile : ""} ${
                  isMobileOnLandscape ? styles.mobileLandscape : ""
                }`}
              >
                {stateMachineState.context.headerTitle}
              </p>
            </MobilePortal>
          </div>
        )}
        {(isProcessingAuto || isPending) && (
          <div className={styles.processStatus}>
            <MobilePortal elementId="mobileTopCurtain">
              <p
                data-cy="cardscanHelperText"
                id={styles.cardscanHelperText}
                className={`${isMobile ? styles.mobile : ""} ${
                  isMobileOnLandscape ? styles.mobileLandscape : ""
                }`}
              >
                {stateMachineState.context.headerTitle}
              </p>
            </MobilePortal>
          </div>
        )}
        {(stateMachineState.matches("eligibilityProcessing") ||
          stateMachineState.matches("postProcessing")) && (
          <div className={styles.processStatus}>
            <div
              data-cy="cardScanCameraLoading"
              className={styles.spinnerWrapper}
            >
              <Spinner {...spinnerOptions} />
            </div>
            <MobilePortal elementId="mobileTopCurtain">
              <p
                data-cy="cardscanHelperText"
                id={styles.cardscanHelperText}
                className={` ${styles.eligibilityText} ${
                  isMobile ? styles.mobile : ""
                } ${isMobileOnLandscape ? styles.mobileLandscape : ""}`}
              >
                {stateMachineState.context.headerTitle}
              </p>
            </MobilePortal>
          </div>
        )}
      </div>
    </>
  );
};

export default ScanFrame;
